<template>
  <div class="heatmap-container">
    <canvas ref="canvas"></canvas>
  </div>
</template>

<script>
export default {
  name: "FootballHeatmap",

  props: {
    points: {
      type: Array,
      default: () => []
    },
    width: {
      type: Number,
      default: 360
    },
    height: {
      type: Number,
      default: 250
    },
    playerPosition: {
      type: String,
      default: ''
    },
    teamSide: {
      type: String,
      default: 'home'
    },
    formation: {
      type: String,
      default: ''
    }
  },

  mounted() {
    this.setupCanvas();
    this.render();
  },

  watch: {
    points: {
      deep: true,
      handler() {
        this.render();
      }
    },
    teamSide() {
      this.render();
    }
  },

  methods: {
    setupCanvas() {
      const canvas = this.$refs.canvas;
      canvas.width = this.width;
      canvas.height = this.height;
      canvas.style.width = this.width + "px";
      canvas.style.height = this.height + "px";
    },

    render() {
      const ctx = this.$refs.canvas.getContext("2d");
      ctx.clearRect(0, 0, this.width, this.height);
      this.drawBackground(ctx); // Full green background including arrow area
      this.drawArrow(ctx);      // Arrow on green background
      this.drawPitch(ctx);      // Pitch lines below arrow
      this.drawHeatmap(ctx);    // Heatmap on pitch
    },

    shouldFlip() {
      if (!this.points.length) return false;
      const avgX = this.points.reduce((sum, p) => sum + p.x, 0) / this.points.length;
      const isHome = this.teamSide === 'home';
      return isHome ? avgX < 50 : avgX > 50;
    },

    // Arrow area is top 26px of canvas
    get ARROW_H() {
      return 26;
    },

    // Draw full green background for entire canvas
    drawBackground(ctx) {
      const w = this.width;
      const h = this.height;

      // Same green as pitch
      ctx.fillStyle = "#437E27";
      ctx.fillRect(0, 0, w, h);
    },

    // Draw arrow on green background at top center
    drawArrow(ctx) {
      const w = this.width;
      const arrowH = 26;
      const isHome = this.teamSide === 'home';

      // Arrow position
      const centerY = arrowH / 2;
      const arrowLength = 60;
      const headSize = 7;
      const centerX = w / 2;

      ctx.save();
      ctx.strokeStyle = "rgba(255, 255, 255, 0.9)";
      ctx.fillStyle = "rgba(255, 255, 255, 0.9)";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";

      if (isHome) {
        // ──────► pointing RIGHT
        const x1 = centerX - arrowLength / 2;
        const x2 = centerX + arrowLength / 2;

        // Line
        ctx.beginPath();
        ctx.moveTo(x1, centerY);
        ctx.lineTo(x2 - headSize, centerY);
        ctx.stroke();

        // Arrowhead
        ctx.beginPath();
        ctx.moveTo(x2, centerY);
        ctx.lineTo(x2 - headSize - 2, centerY - headSize);
        ctx.lineTo(x2 - headSize - 2, centerY + headSize);
        ctx.closePath();
        ctx.fill();

      } else {
        // ◄────── pointing LEFT
        const x1 = centerX + arrowLength / 2;
        const x2 = centerX - arrowLength / 2;

        // Line
        ctx.beginPath();
        ctx.moveTo(x1, centerY);
        ctx.lineTo(x2 + headSize, centerY);
        ctx.stroke();

        // Arrowhead
        ctx.beginPath();
        ctx.moveTo(x2, centerY);
        ctx.lineTo(x2 + headSize + 2, centerY - headSize);
        ctx.lineTo(x2 + headSize + 2, centerY + headSize);
        ctx.closePath();
        ctx.fill();
      }

      ctx.restore();
    },

    drawPitch(ctx) {
      const w = this.width;
      const arrowH = 26; // Pitch starts below arrow area
      const h = this.height - arrowH;
      const top = arrowH;

      // Pitch stripes (vertical)
      for (let i = 0; i < 8; i++) {
        ctx.fillStyle = i % 2 === 0 ? "#437E27" : "#539438";
        ctx.fillRect((w / 8) * i, top, w / 8, h);
      }

      ctx.strokeStyle = "white";
      ctx.lineWidth = 1.8;

      // Border
      ctx.strokeRect(2, top + 2, w - 4, h - 4);

      // Center line
      ctx.beginPath();
      ctx.moveTo(w / 2, top);
      ctx.lineTo(w / 2, top + h);
      ctx.stroke();

      // Center circle
      ctx.beginPath();
      ctx.arc(w / 2, top + h / 2, h * 0.18, 0, Math.PI * 2);
      ctx.stroke();

      // Center spot
      ctx.beginPath();
      ctx.arc(w / 2, top + h / 2, 2, 0, Math.PI * 2);
      ctx.fillStyle = "white";
      ctx.fill();

      // Penalty boxes
      const penaltyBoxWidth = w * 0.18;
      const penaltyBoxHeight = h * 0.5;
      const penaltyBoxY = top + h * 0.25;

      ctx.strokeRect(0, penaltyBoxY, penaltyBoxWidth, penaltyBoxHeight);
      ctx.strokeRect(w - penaltyBoxWidth, penaltyBoxY, penaltyBoxWidth, penaltyBoxHeight);

      // 6-yard boxes
      ctx.strokeRect(0, top + h * 0.37, w * 0.08, h * 0.26);
      ctx.strokeRect(w * 0.92, top + h * 0.37, w * 0.08, h * 0.26);

      // Penalty spots
      const penaltySpotX = w * 0.12;
      const midY = top + h / 2;

      ctx.beginPath();
      ctx.arc(penaltySpotX, midY, 2, 0, Math.PI * 2);
      ctx.fill();

      ctx.beginPath();
      ctx.arc(w - penaltySpotX, midY, 2, 0, Math.PI * 2);
      ctx.fill();

      // Penalty arcs
      const arcRadius = h * 0.18;
      const dx = penaltyBoxWidth - penaltySpotX;
      const angleOffset = Math.acos(dx / arcRadius);

      ctx.beginPath();
      ctx.arc(penaltySpotX, midY, arcRadius, -angleOffset, angleOffset);
      ctx.stroke();

      ctx.beginPath();
      ctx.arc(w - penaltySpotX, midY, arcRadius, Math.PI - angleOffset, Math.PI + angleOffset);
      ctx.stroke();

      // Corner arcs
      const r = 12;

      ctx.beginPath();
      ctx.arc(0, top, r, 0, Math.PI / 2);
      ctx.stroke();

      ctx.beginPath();
      ctx.arc(w, top, r, Math.PI / 2, Math.PI);
      ctx.stroke();

      ctx.beginPath();
      ctx.arc(0, top + h, r, -Math.PI / 2, 0);
      ctx.stroke();

      ctx.beginPath();
      ctx.arc(w, top + h, r, Math.PI, 1.5 * Math.PI);
      ctx.stroke();
    },

    drawHeatmap(ctx) {
      if (!this.points.length) return;

      const arrowH = 26;
      const pitchH = this.height - arrowH;

      const off = document.createElement("canvas");
      off.width = this.width;
      off.height = this.height;
      const octx = off.getContext("2d");

      const radius = 30;
      const kernel = document.createElement("canvas");
      kernel.width = kernel.height = radius * 2;
      const kctx = kernel.getContext("2d");

      const gradient = kctx.createRadialGradient(
        radius, radius, 0,
        radius, radius, radius
      );

      gradient.addColorStop(0,    "rgba(220, 38,  38,  1)");
      gradient.addColorStop(0.2,  "rgba(239, 68,  68,  0.95)");
      gradient.addColorStop(0.35, "rgba(249, 115, 22,  0.85)");
      gradient.addColorStop(0.5,  "rgba(251, 191, 36,  0.7)");
      gradient.addColorStop(0.65, "rgba(253, 224, 71,  0.5)");
      gradient.addColorStop(0.8,  "rgba(254, 240, 138, 0.3)");
      gradient.addColorStop(1,    "rgba(255, 255, 255, 0)");

      kctx.fillStyle = gradient;
      kctx.fillRect(0, 0, radius * 2, radius * 2);

     this.points.forEach(p => {
  let x = p.x;
  let y = p.y;

  // 🔥 HORIZONTAL flip for away team
  if (this.teamSide === 'away') {
    x = 100 - x;
  }

  // 🔥 VERTICAL flip ONLY for home team
  if (this.teamSide === 'home') {
    y = 100 - y;
  }

  const px = (x / 100) * this.width;
  const py = arrowH + (y / 100) * pitchH;

  octx.globalAlpha = 0.35;
  octx.drawImage(kernel, px - radius, py - radius);
});


      // Clip to pitch area only (with small border padding)
      ctx.save();
      ctx.beginPath();
      ctx.rect(4, arrowH + 4, this.width - 8, pitchH - 8);
      ctx.clip();
      
      ctx.drawImage(off, 0, 0);
      
      ctx.restore();
    }
  }
};
</script>

<style scoped>
.heatmap-container {
  display: flex;
  justify-content: center;
  width: 100%;
  overflow: hidden;
}

canvas {
  border-radius: 14px;
  display: block;
  max-width: 100%;
}
</style>